import { useMemo } from "react";
import { omitDataTypes } from "common/entities/entity-column/data-type/functions";
import { queryBuilderBlackList } from "common/entities/entity-column/data-type/types";
import { QueryBuilder } from "common/query-builder";
import { getFields } from "common/query-builder/functions";
import { Select } from "common/query-builder/select";
import { Query, SelectItem } from "common/query/types";
import type { ConditionTypes } from "common/types/condition-type";
import type { Context } from "common/types/context";
import { SchedulerFilter } from "common/types/scheduler-filters";
import { VerticalField } from "common/ui/field";
import type { ValueProps } from "common/with-value-for";

interface PropTypes extends ValueProps<SchedulerFilter> {
  context: Context;
  conditionTypes?: ConditionTypes;
}

const defaultQuery: Query = { select: undefined };
const MAX_SELECT_FIELDS = 5;

export function ConfigurationForm({
  context,
  value,
  conditionTypes,
  onChange,
}: Readonly<PropTypes>) {
  const onChangeSelect = (select: SelectItem[]) => {
    if (!select || select.length <= MAX_SELECT_FIELDS) {
      onChange({ ...value, query: { ...value.query, select } });
    }
  };

  const { entities } = context;

  const fields = useMemo(() => getFields(entities, value), [entities, value]);

  const queryBuilderFilteredFields = useMemo(
    () => omitDataTypes(fields, queryBuilderBlackList),
    [fields],
  );
  const query = value?.query || defaultQuery;

  const selectField = (
    <VerticalField
      label={_("Tile Display Fields")}
      className="x-select qa-select"
      input={
        <Select
          context={context}
          allowAggregates={false}
          forceFkAggregates={true}
          withExpressions={true}
          allowLong={true}
          entities={context.entities}
          fields={queryBuilderFilteredFields}
          maxFields={MAX_SELECT_FIELDS}
          noDuplicates={true}
          value={query.select}
          onChange={onChangeSelect}
        />
      }
    />
  );

  return (
    <QueryBuilder
      context={context}
      withPaging={false}
      conditionTypes={conditionTypes}
      sectionOverrides={{
        from: false,
        select: selectField,
        having: false,
        order: false,
      }}
      value={value}
      onChange={onChange}
    />
  );
}
